import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import {
  History,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react'
import { showBookingPaymentHistory } from '../../../../Redux/ApiCalls/Dashboard/PackageAPI'
import LoadingDots from '../../Utlis/LoadinDots'
import MarketplaceEmptyState from '../../../organisms/Dashboard/Marketplace/MarketplaceEmptyState'
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
} from '../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles'
import {
  ListHeader,
  ListTitle,
  CountBadge,
  TableWrap,
  PaymentsTable,
  AmountCell,
  StatusPill,
  MethodPill,
} from '../CustomerTransaction/transactionTableStyles'

type Payment = {
  id: number
  amount: string
  payment_method: string | null
  status: string
  created_at: string
  description?: string | null
}

type BookingPaymentHistory = {
  id: number
  user: number
  package: number
  booked_price: string
  received_amount: string
  remaining_amount: number
  status: string
  created_at: string
  total_transactions: number
  total_paid_amount: number
  manual_transactions: number
  online_transactions: number
  payments: Payment[]
}


function formatCurrency(value: string | number | null | undefined) {
  if (value === null || value === undefined) return '-'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(num)) return String(value)
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

const ShowBookingTransactionModal = ({
  isOpen,
  bookingId,
  onClose,
}: {
  isOpen: boolean
  bookingId: string
  onClose: () => void
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<BookingPaymentHistory | null>(null)

  const fetchHistory = useCallback(async () => {
    if (!bookingId) return
    try {
      setLoading(true)
      setError(null)
      const res = await showBookingPaymentHistory(bookingId)
      const payload = res as BookingPaymentHistory | undefined
      if (!payload) throw new Error('No data returned')
      setData(payload)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load transactions'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [bookingId])

  useEffect(() => {
    if (!isOpen || !bookingId) return
    fetchHistory()
  }, [isOpen, bookingId, fetchHistory])

  const summary = useMemo(
    () => ({
      booked: formatCurrency(data?.booked_price || 0),
      received: formatCurrency(data?.received_amount || 0),
      remaining: formatCurrency(data?.remaining_amount || 0),
      status: data?.status || '-',
      totalTxns: data?.total_transactions ?? 0,
      onlineTxns: data?.online_transactions ?? 0,
      manualTxns: data?.manual_transactions ?? 0,
    }),
    [data]
  )

  if (!isOpen) return null

  const payments = data?.payments ?? []

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <Shell onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={10} />
                Payment history
              </HeroBadge>
              <ModalTitle>Transaction history</ModalTitle>
              <ModalSubtitle>Booking ID: {bookingId}</ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <Body>
          {loading ? (
            <LoadingWrap>
              <LoadingDots />
              <LoadingHint>Loading transactions…</LoadingHint>
            </LoadingWrap>
          ) : error ? (
            <ErrorBox>
              <div>
                <ErrorTitle>Failed to load data</ErrorTitle>
                <ErrorText>{error}</ErrorText>
              </div>
              <RetryBtn type="button" onClick={fetchHistory}>
                <RefreshCw size={14} />
                Retry
              </RetryBtn>
            </ErrorBox>
          ) : (
            <>
              <StatsGrid>
                <StatCard $tone="#171717">
                  <StatLabel>Booked</StatLabel>
                  <StatValue>₹{summary.booked}</StatValue>
                </StatCard>
                <StatCard $tone="#31a66a">
                  <StatLabel>Received</StatLabel>
                  <StatValue $tone="#31a66a">₹{summary.received}</StatValue>
                </StatCard>
                <StatCard $tone="#e44862">
                  <StatLabel>Remaining</StatLabel>
                  <StatValue $tone="#e44862">₹{summary.remaining}</StatValue>
                </StatCard>
                <StatCard $tone="#6366f1">
                  <StatLabel>Status</StatLabel>
                  <StatusPill $status={summary.status}>
                    {summary.status.charAt(0).toUpperCase() + summary.status.slice(1)}
                  </StatusPill>
                </StatCard>
                <StatCard $tone="#35b7c8">
                  <StatLabel>Total txns</StatLabel>
                  <StatValue>{summary.totalTxns}</StatValue>
                </StatCard>
                <StatCard $tone="#c94daa">
                  <StatLabel>Online / Manual</StatLabel>
                  <StatValue>
                    {summary.onlineTxns} / {summary.manualTxns}
                  </StatValue>
                </StatCard>
              </StatsGrid>

              <TableSection>
                <ListHeader>
                  <ListTitle>Payments</ListTitle>
                  {payments.length > 0 && (
                    <CountBadge>
                      {payments.length} record{payments.length !== 1 ? 's' : ''}
                    </CountBadge>
                  )}
                </ListHeader>

                {payments.length === 0 ? (
                  <MarketplaceEmptyState
                    icon={<History size={32} strokeWidth={1.5} />}
                    title="No payments found"
                    description="Payment records for this booking will appear here once transactions are made."
                    padding="24px 12px"
                  />
                ) : (
                  <TableWrap>
                    <PaymentsTable $minWidth={720}>
                      <thead>
                        <tr>
                          {['Amount', 'Method', 'Status', 'Date', 'Description'].map((h) => (
                            <th key={h}><span>{h}</span></th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment) => (
                          <tr key={payment.id}>
                            <td>
                              <AmountCell>₹{formatCurrency(payment.amount)}</AmountCell>
                            </td>
                            <td>
                              <MethodPill>
                                {payment.payment_method ? payment.payment_method.toUpperCase() : 'N/A'}
                              </MethodPill>
                            </td>
                            <td>
                              <StatusPill $status={payment.status}>
                                {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                              </StatusPill>
                            </td>
                            <td>{formatDate(payment.created_at)}</td>
                            <td>{payment.description?.trim() || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </PaymentsTable>
                  </TableWrap>
                )}
              </TableSection>
            </>
          )}
        </Body>
      </Shell>
    </ModalOverlay>,
    document.body
  )
}

export default ShowBookingTransactionModal

const Shell = styled(ModalShell)`
  max-width: 920px;
  width: 100%;
`

const Body = styled(ModalBody)`
  padding-top: 16px;
  gap: 18px;
`

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 0;
`

const LoadingHint = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`

const ErrorBox = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-radius: 14px;
  background: rgba(228, 72, 98, 0.08);
  border: 1px solid rgba(228, 72, 98, 0.18);
`

const ErrorTitle = styled.p`
  margin: 0 0 4px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #e44862;
`

const ErrorText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(228, 72, 98, 0.85);
`

const RetryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 10px;
  background: #e44862;
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const StatCard = styled.div<{ $tone: string }>`
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 72px;
`

const StatLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.5);
`

const StatValue = styled.span<{ $tone?: string }>`
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: ${(p) => p.$tone || '#171717'};
  line-height: 1.1;
`

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`
